import React, { useEffect, useRef } from 'react';

/**
 * LazyLoadRoute Component
 * Uses IntersectionObserver to trigger route loading when scrolled into view
 *
 * Props:
 * - recommendationId: ID of the recommendation
 * - onVisible: Callback function to call when element becomes visible
 * - children: Content to render
 */
export default function LazyLoadRoute({ recommendationId, onVisible, children }) {
  const elementRef = useRef(null);
  const hasTriggered = useRef(false);

  useEffect(() => {
    const element = elementRef.current;
    if (!element || hasTriggered.current) return;

    // Create intersection observer
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // When element is visible and hasn't been triggered yet
          if (entry.isIntersecting && !hasTriggered.current) {
            hasTriggered.current = true;
            onVisible(recommendationId);
          }
        });
      },
      {
        root: null, // viewport
        rootMargin: '100px', // Start loading 100px before element is visible
        threshold: 0.1 // Trigger when 10% of element is visible
      }
    );

    observer.observe(element);

    // Cleanup
    return () => {
      observer.disconnect();
    };
  }, [recommendationId, onVisible]);

  return <div ref={elementRef}>{children}</div>;
}
